import React, {
  memo, useEffect, useRef, useState
} from 'react';
import { Button, Link } from '@mui/material';
import { useDispatch } from 'react-redux';

import { CardWrapper, FormFieldUpload } from '@monorepo/common';

import { ArrowRight as ArrowRightIcon } from '../../../Icons/arrow-right';
import { usePostLogoMutation, useJobLogoFromSupplierMutation } from '../../../../services/servicePostAJob';
import { showNotification } from '../../../../features/sliceNotification';
import { IEventsStepProps } from '../../interfaceSteps';
import { IStep5Form } from './formInterface';
import { cloudfrontImagesBaseURL } from '../../staticValues';
import UploadLogoTitle from '../../components/UploadLogoTitle';

interface IStep5Props extends IEventsStepProps {
  defaultData:IStep5Form,
  setStep5: (res:IStep5Form) => void,
  linkedSupplierId?: number | null
}
export const Step5 = memo(({
  onNext, onBack, defaultData, setStep5, linkedSupplierId, Nav
}:IStep5Props) => {
  const dispatch = useDispatch();
  const [postLogo, { isLoading }] = usePostLogoMutation();
  const [jobLogoFromSupplier] = useJobLogoFromSupplierMutation();
  const [isUploaded, setIsUploaded] = useState(true);
  const autoFilledSupplierRef = useRef<number | null>(null);

  // When a directory company is linked and the poster hasn't added a logo yet,
  // pull that company's logo in as the default (the server copies it into the job
  // image bucket so it saves/displays like any uploaded logo). Once per supplier.
  useEffect(() => {
    const sid = linkedSupplierId;
    if (!sid || sid <= 0 || autoFilledSupplierRef.current === sid) {
      return;
    }
    autoFilledSupplierRef.current = sid;
    if (defaultData?.uploadedLogoPath) {
      return; // don't clobber a logo the user already has
    }
    jobLogoFromSupplier({ supplierId: sid })
      .then((res) => {
        if (res && 'data' in res && res.data && res.data.url) {
          setStep5({
            ...defaultData,
            uploadedLogoPath: `${cloudfrontImagesBaseURL}${res.data.url}`
          });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedSupplierId]);

  const change = async (file: File | File[]) => {
    if (!Array.isArray(file)) {
      const formData = new FormData();
      formData.append('logo', file);
      // if server returned headshot applicationURL, all will send to server for save
      await postLogo(formData)
        .then((res) => {
          if (res && 'data' in res) {
            const { url } = res.data as { url: string };
            setIsUploaded(true);
            setStep5({
              ...defaultData,
              uploadedLogoPath: `${cloudfrontImagesBaseURL}${url}`
            });
            dispatch(showNotification({
              type: 'success',
              show: true,
              text: 'Logo was uploaded!'
            }));
            return;
          }

          setIsUploaded(false);
          dispatch(showNotification({
            type: 'error',
            show: true,
            text: 'Logo not saved!'
          }));
        });
    }
  };
  const onRemove = (status: boolean) => {
    if (status) {
      setStep5({
        ...defaultData,
        uploadedLogoPath: null
      });
    }
  };

  const next = () => {
    if (onNext) {
      if (!isUploaded) {
        setStep5({
          ...defaultData,
          uploadedLogoPath: null
        });
      }
      onNext();
    }
  };

  return (
    <CardWrapper
      classNameForInternalBlock="cpg-step-card"
      isLoading={isLoading}
      dataCy="step5"
    >
      <UploadLogoTitle />

      <FormFieldUpload
        onChange={change}
        maxFilesUpload={1}
        onRemove={onRemove}
        removeLabel="Remove logo"
        classNameForWrap="cpg-dropzone"
        classNameForImage="cpg-logo-preview"
        maxSizeBytes={10485760} // max file size 10mb
        defaultValue={defaultData?.uploadedLogoPath && defaultData.uploadedLogoPath}
        uploadAreaSettings={{
          image: (
            <span
              className="cpg-dropzone__icon"
              aria-hidden="true"
            >
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 15V4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 9l5-5 5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          ),
          mainTitle: (
            <>
              Drag & drop your logo or
              {' '}
              <Link underline="always">Browse your files</Link>
            </>
          ),
          mainSubtitleTitle: (
            <span className="cpg-dropzone__hint">PNG or JPG · up to 10 MB</span>
          )
        }}
      />

      <Nav
        nextDisabled={isLoading}
        onBack={onBack}
        textPreviousStep="← Back"
        nextButton={(
          <Button
            endIcon={(<ArrowRightIcon fontSize="small" />)}
            variant="contained"
            disabled={false}
            type="button"
            onClick={next}
            data-cy="nextStep"
          >
            Next step
          </Button>
            )}
      />
    </CardWrapper>
  );
});
